/**
 * Every string a reader sees on the evidence surfaces: the drawer that holds one value's
 * records, the decision cluster that sorts each value, and the foot that counts what has been
 * sorted. One literal, because the same sentence is said on more than one of them and a second
 * copy is how two surfaces end up promising different things about one press.
 *
 * The apostrophe is U+2019 throughout, matching the phrase the drawer's specs pin. No em or en
 * dash anywhere, and nothing here says a job is done: a scan that is never stored has no
 * completed state to report.
 *
 * The source-kind vocabulary is NOT here. It is keyed on the vendor catalogue's own enum and
 * lives with that enum in `footprint-sources.ts`; a second copy of those words would be a
 * second answer to "what kind of place is this".
 */

import { formatNumber } from '@privacy-bee/format/number';
import type { FootprintValue, FragmentState, ScanDeferralReason } from '~/types/person/footprint';
import { isHashedCredential } from '~/utils/footprint-credentials';

export const EVIDENCE_COPY = {
    deferral: {
        account_limit: 'The daily search limit for our dark web data source has been reached.',
        source_unavailable: 'A dark web data source is temporarily unavailable. You can try again now.',
        disabled: 'Dark web scanning is temporarily unavailable.',
    } satisfies Record<ScanDeferralReason, string>,

    /**
     * Where one value stands, keyed on `FragmentState`. Every surface that names a state — the
     * fragment row's pill, the drawer header, the family pill's bare word — reads these, so they
     * can never disagree about a value the reader can see in two places at once.
     *
     * Each word names where the VALUE stands, never how the reader feels about it: the reader's
     * own state is not a property of the fragment, and a word for it reads as a verdict on them
     * rather than on the evidence.
     */
    state: {
        vault: 'In your vault',
        me: 'That’s Me',
        familiar: 'Familiar',
        family: 'Family',
        excluded: 'Not yours',
        undecided: 'Undecided',
        credential: 'Exposed credential',
    },

    /**
     * The credential state's word for a password the source classed as a hash, said in place of
     * `state.credential` on every surface that names that state: a hash may not be the password,
     * so it is never shown under a word that says it is. Read through `fragmentStateLabel`.
     */
    hashedCredential: 'Exposed as a hash',

    drawer: {
        close: 'Close detail',
        add: 'Add to my vault',
        adding: 'Adding…',
        /** One sentence pair per state of the drilled value, each true only for that state. */
        hint: {
            inVault:
                'It’s in your vault, so we already watch for it as yours. The records below are where it has turned up, and anything else riding in them is yours to sort.',
            addable:
                'Mark it as yours for this scan. You can review and save it in the vault form that opens. Saving is optional.',
            notAddable:
                'Mark it as yours for this scan, even when your vault can’t store it. Nothing is added to your vault here.',
        },
    },

    record: {
        verified: 'Verified breach',
        line: (n: number) => `line ${n}`,
        incident: (monthYear: string) => `Incident ${monthYear}`,
        incidentUnknown: 'Incident date unknown',
        published: (monthYear: string) => `Published ${monthYear}`,
        records: (n: number) => `${formatNumber(n)} ${n === 1 ? 'record' : 'records'}`,
        more: 'More',
        less: 'Less',
        /** Said only to a screen reader, so the visible word is what the control's name starts with. */
        moreSuffix: ' about this source',
        tags: 'Tags',
        password: {
            label: 'Password',
            plaintext: 'plaintext',
            view: 'View exposed password',
            hashed: (hashType: string | null) => (hashType ? `hashed (${hashType})` : 'hashed'),
        },
        inVaultMark: 'In your vault',
        /** A record page carries a credential's presence and never its value. */
        hidden: {
            password: '•••••• (hidden)',
            ssn: '•••-••-•••• (hidden)',
        },
    },

    /**
     * The action word leads, because voice control matches a spoken command against the
     * control's accessible name and the value is what tells two rows apart.
     */
    cluster: {
        me: (display: string) => `Claim ${display} as mine`,
        familiar: (display: string) => `Mark ${display} as familiar`,
        excluded: (display: string) => `Mark ${display} as not yours`,
    },

    /**
     * What a decision did, said where the reader is looking. Each names the consequence, and
     * `cleared` says only that the value is undecided again: a record field the snapshot cap
     * left out has no level list to go back into, and neither has a row a descent has hidden.
     */
    toast: {
        me: (display: string) => `Marked “${display}” as yours for this scan.`,
        familiar: (display: string) => `Marked “${display}” as familiar. It stays out of your vault.`,
        excluded: (display: string) => `Ruled out “${display}” for this journey. It isn’t saved as a permanent exclusion.`,
        cleared: (display: string) => `Cleared your choice on “${display}”. It’s undecided again.`,
        undo: 'Undo',
    },

    foot: {
        count: {
            vault: 'From Your Vault',
            me: 'Yours',
            family: 'Family',
            familiar: 'Familiar',
            excluded: 'Not Yours',
        },
        newTab: 'opens in a new tab',
    },
} as const;

/**
 * What a state pill says about one value, on the result row and in the drawer header alike: the
 * state's own word, except that a credential the source classed as a hash is labeled a hash.
 */
export const fragmentStateLabel = (state: FragmentState, value: FootprintValue | null) =>
    state === 'credential' && isHashedCredential(value) ? EVIDENCE_COPY.hashedCredential : EVIDENCE_COPY.state[state];
